import { isReduced, OPTIC, RECEDE, SPRING_SURFACE } from './motion';

/**
 * A surface that grows out of the control that opened it — the same object
 * changing form, not a second one appearing beside it.
 *
 * The surface (`body`) is drawn first at the source's rectangle, radius and
 * colour, then interpolates its size, position, radius, material and
 * elevation to its own resting form; its contents come into focus once there
 * is room for them. Closing is the reverse: the surface folds back into the
 * source and is gone exactly where the source is.
 *
 * Every change starts from wherever the surface is drawn at that moment, so
 * reopening while it folds away (or the reverse) simply turns it around.
 * While it moves, its wrapper keeps the resting size so placement logic never
 * sees the surface changing.
 */

const ID = 'mytxt-grow';
export const GROW_CLOSE_MS = 300;

interface Look {
  rect: DOMRect;
  radius: string;
  background: string;
  shadow: string;
  opacity: string;
}

function look(el: Element): Look {
  const cs = getComputedStyle(el);
  return { rect: el.getBoundingClientRect(), radius: cs.borderTopLeftRadius, background: cs.backgroundColor, shadow: cs.boxShadow, opacity: cs.opacity };
}

const moving = (el: Element) => el.getAnimations().some((a) => a.id === ID);

function stop(...els: Array<Element | null | undefined>) {
  for (const el of els) for (const a of el?.getAnimations() ?? []) if (a.id === ID) a.cancel();
}

const PROPS = ['position', 'left', 'top', 'bottom', 'overflow', 'min-width'];

/** Back to the surface's authored form: no fixed sizes, no animation. */
function release(wrapper: HTMLElement, body: HTMLElement, content: HTMLElement | null) {
  stop(body, content);
  wrapper.style.removeProperty('width');
  wrapper.style.removeProperty('height');
  for (const p of PROPS) body.style.removeProperty(p);
  content?.style.removeProperty('width');
}

/** Hold the wrapper at the surface's resting size and let the body move freely inside it. */
function hold(wrapper: HTMLElement, body: HTMLElement, content: HTMLElement | null, rest: DOMRect, fromBelow: boolean) {
  if (content && !content.style.width) content.style.width = `${content.getBoundingClientRect().width}px`;
  wrapper.style.width = `${rest.width}px`;
  wrapper.style.height = `${rest.height}px`;
  body.style.position = 'absolute';
  body.style.left = '0';
  body.style.overflow = 'hidden';
  body.style.minWidth = '0';
  if (fromBelow) {
    body.style.top = 'auto';
    body.style.bottom = '0';
  } else body.style.top = '0';
}

const box = (r: DOMRect, rest: DOMRect, fromBelow: boolean) => ({
  width: `${r.width}px`,
  height: `${r.height}px`,
  transform: `translate(${r.left - rest.left}px, ${fromBelow ? r.bottom - rest.bottom : r.top - rest.top}px)`,
});

/** Grow `body` (inside `wrapper`) out of `source`. Resolves when it rests. */
export function growOpen(wrapper: HTMLElement, body: HTMLElement, source: Element | null): Promise<void> {
  const content = body.firstElementChild as HTMLElement | null;
  if (isReduced() || !source?.isConnected) {
    release(wrapper, body, content);
    return Promise.resolve();
  }
  // Already part-way (folding away): turn around from there. Otherwise start as the source itself.
  const turning = moving(body);
  const from = turning ? look(body) : { ...look(source), shadow: 'none', opacity: '1' };
  const contentFrom = turning && content ? getComputedStyle(content).opacity : '0';
  release(wrapper, body, content);
  const to = look(body);
  const rest = wrapper.getBoundingClientRect();
  // A surface placed above its control grows upward from it.
  const fromBelow = to.rect.top < from.rect.top;
  hold(wrapper, body, content, rest, fromBelow);

  // Geometry follows the surface spring; the material becomes the surface's own early, so it never reads as a big key.
  const a = body.animate(
    [
      { ...box(from.rect, rest, fromBelow), borderRadius: from.radius },
      { ...box(to.rect, rest, fromBelow), borderRadius: to.radius },
    ],
    { duration: SPRING_SURFACE.ms, easing: SPRING_SURFACE.css, fill: 'both' },
  );
  a.id = ID;
  const m = body.animate(
    [
      { backgroundColor: from.background, boxShadow: from.shadow, opacity: from.opacity },
      { backgroundColor: to.background, boxShadow: to.shadow, opacity: '1' },
    ],
    { duration: 200, easing: OPTIC, fill: 'both' },
  );
  m.id = ID;
  if (content) {
    const c = content.animate(
      [
        { opacity: contentFrom, filter: 'blur(4px)', transform: 'translateY(-3px)' },
        { opacity: '1', filter: 'blur(0px)', transform: 'none' },
      ],
      { duration: 420, delay: turning ? 0 : 70, easing: OPTIC, fill: 'both' },
    );
    c.id = ID;
  }
  return a.finished.then(
    () => release(wrapper, body, content),
    () => {},
  );
}

/** Fold `body` back into `source`. It stays at the source, invisible, until it is removed. */
export function growClose(wrapper: HTMLElement, body: HTMLElement, source: Element | null): void {
  const content = body.firstElementChild as HTMLElement | null;
  if (isReduced()) return;
  const from = look(body);
  const contentFrom = content ? getComputedStyle(content).opacity : '1';
  // Mid-flight the wrapper is already held at the resting size; at rest it simply is that size.
  const rest = wrapper.getBoundingClientRect();
  stop(body, content);
  const target = source?.isConnected ? { ...look(source), shadow: 'none' } : null;
  const fromBelow = !!target && target.rect.top > from.rect.bottom - 1;
  hold(wrapper, body, content, rest, fromBelow);

  const end = target
    ? { ...box(target.rect, rest, fromBelow), borderRadius: target.radius }
    : { ...box(new DOMRect(from.rect.left + from.rect.width * 0.03, from.rect.top, from.rect.width * 0.94, from.rect.height * 0.94), rest, fromBelow) };
  const a = body.animate([{ ...box(from.rect, rest, fromBelow), borderRadius: from.radius }, end], { duration: GROW_CLOSE_MS, easing: 'cubic-bezier(0.5, 0, 0.2, 1)', fill: 'both' });
  a.id = ID;
  // It keeps the surface's material until it is nearly the source, takes the source's on the last stretch, and hands over to it.
  const bg = target?.background ?? from.background;
  const m = body.animate(
    [
      { backgroundColor: from.background, boxShadow: from.shadow, opacity: from.opacity },
      { backgroundColor: from.background, opacity: '1', offset: 0.5 },
      { backgroundColor: bg, boxShadow: 'none', opacity: '1', offset: 0.86 },
      { backgroundColor: bg, boxShadow: 'none', opacity: '0' },
    ],
    { duration: GROW_CLOSE_MS, easing: 'linear', fill: 'both' },
  );
  m.id = ID;
  if (content) {
    const c = content.animate([{ opacity: contentFrom, filter: 'blur(0px)' }, { opacity: '0', filter: 'blur(4px)' }], { duration: 130, easing: RECEDE, fill: 'both' });
    c.id = ID;
  }
}
